import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useEditCalendarMeetingAttendeeInfoMutation } from '../../api/calendar.js';
import IconEdit from '../../components/images/IconEdit.jsx';
import IconSave from '../../components/images/IconSave.jsx';
import { useErrorToast } from '../../hooks/useToast.jsx';
import BaseButton from '../form/BaseButton.jsx';
import BaseOverlay from '../layout/BaseOverlay.jsx';
import ReactMarkdown from 'react-markdown';

const MeetingAttendeeInfoEdit = ({
    calendarMeetingId,
    attendeeId,
    attendeeCustomInfo,
    attendeeAiInfo,
    showOverlay,
    setShowOverlay,
    noEdit = false
}) => {
    const [attendeeState, setAttendeeState] = useState('');
    const [isEditing, setIsEditing] = useState(false);

    const attendeeToEdit =
        attendeeCustomInfo !== null && attendeeCustomInfo?.trim() !== '' ? attendeeCustomInfo : attendeeAiInfo;

    const [editCalendarMeetingAttendeeInfo, { isLoading, error }] = useEditCalendarMeetingAttendeeInfoMutation();

    useEffect(() => {
        setAttendeeState(attendeeToEdit);
    }, [attendeeToEdit]);

    const closeOverlay = () => {
        setIsEditing(false);
        setShowOverlay(false);
    };

    const handleAttendeeChange = (e) => {
        setAttendeeState(e.target.value);
    };

    useEffect(() => {
        if (error) {
            useErrorToast(error?.data?.message || 'An error occurred while saving the attendee info.');
        }
    }, [error]);

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSaveEdit = () => {
        setIsEditing(false);
    };

    const saveAttendee = async () => {
        const requestData = { info: attendeeState };
        await editCalendarMeetingAttendeeInfo({ meetingId: calendarMeetingId, id: attendeeId, data: requestData });
        closeOverlay();
    };

    const overlayContent = (
        <BaseOverlay show={showOverlay} className='overflow-auto h-[100dvh] z-[1002] rounded-none'>
            <div className='min-h-96 max-w-3xl w-full mx-auto px-4'>
                <div className='flex justify-end py-4'>
                    <div className='text-theme1-primary font-bold' onClick={closeOverlay}>
                        Close
                    </div>
                </div>
                <div className='text-center text-theme1-title2 font-bold text-[22px] mb-3'>Attendee Info</div>

                <div className='flex justify-between items-start mb-4 mt-4'>
                    {isEditing ? (
                        <textarea
                            className='w-[90%] sm:w-[95%] text-theme1-text1 p-4 border border-theme1-border1 rounded-lg h-[350px] focus:outline-none focus:ring-2 focus:ring-theme1-primary'
                            value={attendeeState}
                            onChange={handleAttendeeChange}
                            placeholder='Edit attendee info'
                        />
                    ) : (
                        <span className='text-theme1-dark text-[16px]  whitespace-pre-wrap w-[90%] sm:w-[95%]'>
                            <ReactMarkdown>{attendeeState}</ReactMarkdown>
                        </span>
                    )}

                    {!noEdit && (
                        <>
                            {isEditing ? (
                                <button onClick={handleSaveEdit}>
                                    <IconSave className='w-6 h-6 fill-theme1-goldDark' />
                                </button>
                            ) : (
                                <button onClick={handleEdit}>
                                    <IconEdit className='w-6 h-6 stroke-theme1-goldDark' />
                                </button>
                            )}
                        </>
                    )}
                </div>

                {!noEdit && isEditing ? (
                    <BaseButton
                        className='mt-4 mb-5 btn-red-outline'
                        onClick={saveAttendee}
                        textValue={isLoading ? 'Saving...' : 'Save changes'}
                        disabled={isLoading}
                    />
                ) : null}
            </div>
        </BaseOverlay>
    );

    return <>{createPortal(overlayContent, document.body)}</>;
};

export default MeetingAttendeeInfoEdit;
